<template>
  <div class="results-table-container">
    <a-table
      :columns="columns"
      :data-source="dataSource"
      :loading="loading"
      :pagination="pagination"
      :scroll="{ x: 1600 }"
      row-key="_id"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'status'">
          <a-tag :color="getStatusColor(record.status)">
            {{ getStatusText(record.status) }}
          </a-tag>
        </template>

        <template v-else-if="column.key === 'mobile'">
          {{ formatMobile(record.mobile) }}
        </template>

        <template v-else-if="column.key === 'createdAt'">
          {{ formatDate(record.createdAt) }}
        </template>

        <template v-else-if="column.key === 'uid'">
          <a-typography-text 
            :copyable="{ tooltips: ['복사', '복사됨'] }" 
            :ellipsis="{ tooltip: true }"
            style="display: inline-block; width: 100%;"
          >
            {{ record.uid || '-' }}
          </a-typography-text>
        </template>

        <template v-else-if="column.key === 'cid'">
          <a-typography-text 
            :copyable="{ tooltips: ['복사', '복사됨'] }" 
            :ellipsis="{ tooltip: true }"
            style="display: inline-block; width: 100%;"
          >
            {{ record.cid || '-' }}
          </a-typography-text>
        </template>

        <template v-else-if="column.key === 'processingTime'">
          <span v-if="record.processingTime">{{ record.processingTime }} ms</span>
          <span v-else>-</span>
        </template>

        <template v-else-if="column.key === 'action'">
          <a-button
            size="small"
            @click="handleViewDetail(record)"
          >
            상세보기
          </a-button>
        </template>
      </template>
    </a-table>

    <div class="table-footer">
      <div class="summary-info">
        <a-space>
          <span>전체: {{ totalCount }}건</span>
        </a-space>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import dayjs from 'dayjs';
import { STATUS_COLOR_MAP, STATUS_TEXT_MAP } from '../alimtalkApi';
import { message, Typography } from 'ant-design-vue';

export default {
  name: 'AlimtalkResultsTable',
  components: {
    'a-typography-text': Typography.Text
  },
  props: {
    dataSource: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    total: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 20
    }
  },
  emits: ['change', 'viewDetail'],
  setup(props, { emit }) {
    const columns = [
      {
        title: '발송일시',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 180,
        sorter: true
      },
      {
        title: '메시지 전송유형',
        dataIndex: 'eventType',
        key: 'eventType',
        width: 200
      },
      {
        title: '수신번호',
        dataIndex: 'mobile',
        key: 'mobile',
        width: 140
      },
      {
        title: '템플릿 코드',
        dataIndex: 'templateCode',
        key: 'templateCode',
        width: 180
      },
      {
        title: 'UID',
        dataIndex: 'uid',
        key: 'uid',
        width: 250
      },
      {
        title: 'CID',
        dataIndex: 'cid',
        key: 'cid',
        width: 250
      },
      {
        title: '처리시간',
        dataIndex: 'processingTime',
        key: 'processingTime',
        width: 100,
        align: 'center',
        sorter: true
      },
      {
        title: '상태',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        align: 'center',
        filters: [
          { text: '성공', value: 'SUCCESS' },
          { text: '실패', value: 'FAILED' },
          { text: '오류', value: 'ERROR' }
        ]
      },
      {
        title: '작업',
        key: 'action',
        fixed: 'right',
        width: 100,
        align: 'center'
      }
    ];

    const pagination = computed(() => ({
      total: props.total,
      current: props.currentPage,
      pageSize: props.pageSize,
      showSizeChanger: true,
      showTotal: (total) => `총 ${total}건`,
      pageSizeOptions: ['10', '20', '50', '100']
    }));

    const totalCount = computed(() => props.total);

    const formatMobile = (mobile) => {
      if (!mobile) return '-';
      const cleaned = mobile.replace(/[^0-9]/g, '');
      if (cleaned.length === 11) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
      }
      return mobile;
    };

    const formatDate = (date) => {
      if (!date) return '-';
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
    };

    const getStatusColor = (status) => {
      return STATUS_COLOR_MAP[status] || '#d9d9d9';
    };

    const getStatusText = (status) => {
      return STATUS_TEXT_MAP[status] || status;
    };

    const handleTableChange = (paginationInfo, filters, sorter) => {
      const params = {
        page: paginationInfo.current,
        limit: paginationInfo.pageSize
      };

      // 정렬 처리
      if (sorter.field && sorter.order) {
        params.sort = sorter.order === 'ascend' ? sorter.field : `-${sorter.field}`;
      }

      // 필터 처리
      if (filters.status && filters.status.length > 0) {
        params.status = filters.status;
      }

      emit('change', params);
    };

    const handleViewDetail = (record) => {
      emit('viewDetail', record);
    };


    return {
      columns,
      pagination,
      totalCount,
      formatMobile,
      formatDate,
      getStatusColor,
      getStatusText,
      handleTableChange,
      handleViewDetail
    };
  }
};
</script>

<style scoped>
.results-table-container {
  background: transparent;
  padding: 0;
}

.table-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.summary-info {
  font-size: 14px;
}

:deep(.ant-table-cell) {
  font-size: 13px;
}

:deep(.ant-btn-sm) {
  font-size: 12px;
}
</style>